import React, { useEffect, useState } from 'react';
import { Mail, Plus, RefreshCw } from 'lucide-react';
import ThemedSelect from '../components/ThemedSelect';
import AdminServiceNoticeRecipients from '../components/AdminServiceNoticeRecipients';
import ConfirmModal from '../components/ConfirmModal';
import { useNotification } from '../components/NotificationProvider';
import { AdminLegalDocument, fetchAdminLegalDocuments } from '../services/admin.api';
import {
  ServiceNotice,
  ServiceNoticeType,
  fetchServiceNotices,
  prepareServiceNotice,
  sendServiceNotice,
  pauseServiceNotice,
  retryServiceNotice,
  deleteServiceNoticeDraft,
  resolveServiceNoticeIssue,
} from '../services/serviceNotices';
import './AdminLegalDocuments.css';
import './AdminServiceNotices.css';

type NoticeConfirmation = 'send' | 'retry' | 'delete';

const localDate = (date: Date) => {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const dateLabel = (date: string) => {
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(date));
};

const subjectFor = (type: ServiceNoticeType) => {
  return type === 'TERMS_CHANGE'
    ? 'Actualizăm Termenii și condițiile Roade'
    : 'Roade își încheie activitatea';
};

const noticeStatusLabel = (notice: ServiceNotice) => {
  if (!notice.startedAt) {
    return 'Ciornă';
  }
  if (notice.paused) {
    return 'Pe pauză';
  }
  if (notice.pending + notice.sending > 0) {
    return 'În curs';
  }
  if (notice.failed + notice.uncertain > 0) {
    return 'De verificat';
  }
  return 'Finalizată';
};

const confirmationMessage = (action: NoticeConfirmation, notice: ServiceNotice) => {
  switch (action) {
    case 'delete':
      return 'Nu a fost trimis niciun email. Ciorna și lista pregătită vor fi șterse.';
    case 'retry':
      return `Vor fi reîncercate doar cele ${notice.failed} trimiteri respinse. Mesajele cu rezultat incert sunt excluse.`;
    case 'send':
      return `Emailul previzualizat va fi trimis individual către ${notice.pending} conturi rămase. Verifică mesajul, data și preavizul. Emailurile trimise nu pot fi retrase.`;
  }
};

const AdminServiceNotices: React.FC = () => {
  const { showNotification } = useNotification();
  const [notices, setNotices] = useState<ServiceNotice[]>([]);
  const [documents, setDocuments] = useState<AdminLegalDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [type, setType] = useState<ServiceNoticeType>('TERMS_CHANGE');
  const [subject, setSubject] = useState(subjectFor('TERMS_CHANGE'));
  const [message, setMessage] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [closureDate, setClosureDate] = useState(() => localDate(new Date(Date.now() + 32 * 86400000)));
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState<NoticeConfirmation | null>(null);
  const [resolution, setResolution] = useState<{
    recipientId: string;
    outcome: 'ACCEPTED' | 'NOT_SENT';
  } | null>(null);
  const selected = notices.find((n) => n.id === selectedId);
  const scheduledTerms = documents.filter((d) => d.documentType === 'TERMS' && d.status === 'SCHEDULED');
  const document = scheduledTerms.find((d) => d.id === documentId);

  useEffect(() => {
    let active = true;
    Promise.all([fetchServiceNotices(), fetchAdminLegalDocuments()])
      .then(([loadedNotices, loadedDocuments]) => {
        if (active) {
          setNotices(loadedNotices);
          setDocuments(loadedDocuments);
        }
      })
      .catch(() => {
        if (active) {
          showNotification('Nu am putut încărca informările. Încearcă din nou.', 'error');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [showNotification]);

  useEffect(() => {
    const hasActiveDeliveries = notices.some((notice) => {
      return notice.sending > 0 || (notice.startedAt && !notice.paused && notice.pending > 0);
    });
    if (!hasActiveDeliveries) {
      return;
    }
    let active = true;
    const timer = window.setInterval(() => {
      fetchServiceNotices()
        .then((loadedNotices) => {
          if (active) {
            setNotices(loadedNotices);
          }
        })
        .catch(() => {
          // Keep the last known progress until the next refresh succeeds.
        });
    }, 5000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [notices]);

  const update = (notice: ServiceNotice) => {
    setNotices((currentNotices) => [notice, ...currentNotices.filter((current) => current.id !== notice.id)]);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
      setConfirmation(null);
    } catch {
      showNotification(
        'Operațiunea nu a reușit. Verifică data, versiunea programată și starea informării, apoi reîmprospătează.',
        'error'
      );
    } finally {
      setBusy(false);
    }
  };

  const prepare = (event: React.FormEvent) => {
    event.preventDefault();
    if (type === 'TERMS_CHANGE' && !document) {
      return;
    }
    const effectiveAt =
      document && type === 'TERMS_CHANGE' ? document.effectiveAt : new Date(closureDate).toISOString();
    const minimumDays = type === 'TERMS_CHANGE' ? 15 : 30;
    if (new Date(effectiveAt).getTime() < Date.now() + minimumDays * 86400000) {
      showNotification(`Alege o dată cu cel puțin ${minimumDays} zile înainte de aplicare.`, 'error');
      return;
    }
    void run(async () => {
      const notice = await prepareServiceNotice({
        id: requestId,
        type,
        subject: subject.trim(),
        message: message.trim(),
        effectiveAt,
        documentId: type === 'TERMS_CHANGE' ? documentId : null,
      });
      update(notice);
      setSelectedId(notice.id);
    });
  };

  const refreshNotices = () => {
    void run(async () => {
      const loadedNotices = await fetchServiceNotices();
      setNotices(loadedNotices);
    });
  };

  const pauseSelectedNotice = () => {
    if (!selected) {
      return;
    }
    void run(async () => {
      const pausedNotice = await pauseServiceNotice(selected.id);
      update(pausedNotice);
    });
  };

  const confirmNoticeAction = () => {
    if (!selected || !confirmation) {
      return;
    }
    void run(async () => {
      if (confirmation === 'delete') {
        await deleteServiceNoticeDraft(selected.id);
        setNotices((currentNotices) => currentNotices.filter((notice) => notice.id !== selected.id));
        setSelectedId(null);
        setRequestId(crypto.randomUUID());
        return;
      }

      let updatedNotice: ServiceNotice;
      if (confirmation === 'retry') {
        updatedNotice = await retryServiceNotice(selected.id);
      } else {
        updatedNotice = await sendServiceNotice(selected.id);
      }
      update(updatedNotice);
    });
  };

  const confirmRecipientResolution = () => {
    if (!selected || !resolution) {
      return;
    }
    void run(async () => {
      const resolvedNotice = await resolveServiceNoticeIssue(
        selected.id,
        resolution.recipientId,
        resolution.outcome
      );
      update(resolvedNotice);
      setResolution(null);
    });
  };

  return (
    <section className="admin-service-notices admin-legal-documents">
      <div className="admin-legal-header">
        <div>
          <span className="notice-eyebrow">Comunicări</span>
          <h2>Informări prin email</h2>
          <p>Anunță din timp modificarea Termenilor sau închiderea platformei.</p>
        </div>
        <div className="notice-actions">
          <button type="button" className="admin-legal-secondary" disabled={busy} onClick={refreshNotices}>
            <RefreshCw aria-hidden="true" />
            Reîmprospătează
          </button>
          <button
            type="button"
            className="admin-legal-primary"
            disabled={busy}
            onClick={() => {
              setSelectedId(null);
              setRequestId(crypto.randomUUID());
              setMessage('');
            }}
          >
            <Plus aria-hidden="true" />
            Informare nouă
          </button>
        </div>
      </div>
      <p className="notice-help">
        Mesajele sunt trimise individual conturilor cu email verificat, inclusiv celor blocate, indiferent de
        opțiunile de marketing. Administratorii sunt excluși.
      </p>
      <p className="notice-background-info">
        Trimiterea continuă în fundal chiar dacă închizi pagina. Revino la istoricul informărilor pentru
        progres și starea fiecărui mesaj.
      </p>
      {loading ? (
        <p role="status">Se încarcă informările...</p>
      ) : (
        !selected && (
          <form className="notice-form" onSubmit={prepare}>
            <span className="notice-field-label">Tipul informării</span>
            <ThemedSelect<ServiceNoticeType>
              ariaLabel="Tipul informării"
              value={type}
              disabled={busy}
              options={[
                { value: 'TERMS_CHANGE', label: 'Modificarea Termenilor și condițiilor' },
                { value: 'APP_CLOSURE', label: 'Închiderea platformei' },
              ]}
              onChange={(next) => {
                setType(next);
                setSubject(subjectFor(next));
                setMessage('');
              }}
            />
            {type === 'TERMS_CHANGE' ? (
              <>
                <span className="notice-field-label">Versiunea programată</span>
                <ThemedSelect
                  ariaLabel="Versiunea programată"
                  value={documentId}
                  disabled={busy}
                  options={[
                    { value: '', label: 'Alege versiunea' },
                    ...scheduledTerms.map((d) => ({
                      value: d.id,
                      label: `${d.documentVersion} · ${dateLabel(d.effectiveAt)}`,
                    })),
                  ]}
                  onChange={(value) => {
                    setDocumentId(value);
                    setMessage(scheduledTerms.find((d) => d.id === value)?.changeSummary ?? '');
                  }}
                />
                <p className="notice-help">
                  Pregătește și programează mai întâi versiunea în „Documente legale”, cu cel puțin 15 zile
                  înainte. Alege un interval mai lung pentru timpul necesar trimiterii și eventualelor erori.
                </p>
              </>
            ) : (
              <>
                <label htmlFor="notice-date">Data și ora închiderii</label>
                <input
                  id="notice-date"
                  type="datetime-local"
                  required
                  value={closureDate}
                  min={localDate(new Date(Date.now() + 30 * 86400000))}
                  disabled={busy}
                  onChange={(e) => setClosureDate(e.target.value)}
                />
                <p className="notice-help">
                  Planifică cel puțin 30 de zile de preaviz. Informarea nu închide și nu șterge automat
                  platforma.
                </p>
              </>
            )}
            <label htmlFor="notice-subject">Subiect</label>
            <input
              id="notice-subject"
              required
              maxLength={180}
              value={subject}
              disabled={busy}
              onChange={(e) => setSubject(e.target.value)}
            />
            <label htmlFor="notice-message">Mesajul pentru utilizatori</label>
            <textarea
              id="notice-message"
              required
              maxLength={10000}
              rows={7}
              value={message}
              disabled={busy}
              onChange={(e) => setMessage(e.target.value)}
            />
            <p className="notice-help">
              {type === 'TERMS_CHANGE'
                ? 'Explică modificările și motivele lor. Emailul va include data aplicării și un link către copia exactă a noilor Termeni.'
                : 'Explică motivul închiderii, ce date vor fi șterse sau păstrate și până când pot fi solicitate exporturile.'}
            </p>
            <button
              className="admin-legal-primary"
              disabled={busy || !subject.trim() || !message.trim() || (type === 'TERMS_CHANGE' && !document)}
            >
              <Mail aria-hidden="true" />
              Pregătește și previzualizează
            </button>
            <p className="notice-help">Acest pas nu trimite niciun email.</p>
          </form>
        )
      )}
      {selected && (
        <div className="notice-preview">
          <h3>{selected.subject}</h3>
          <p>
            Data anunțată: {dateLabel(selected.effectiveAt)} · {selected.recipientCount} destinatari la
            pregătire
          </p>
          <dl className="notice-progress" aria-label="Progresul trimiterii">
            {(
              [
                ['În așteptare', selected.pending],
                ['În curs', selected.sending],
                ['Acceptate de furnizor', selected.accepted],
                ['Respinse', selected.failed],
                ['Rezultat incert', selected.uncertain],
                ['Omise / conturi șterse', selected.skipped],
              ] as const
            ).map(([label, count]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{count}</dd>
              </div>
            ))}
          </dl>
          <p className="notice-help">
            Acceptarea de către furnizor nu confirmă livrarea sau citirea emailului. Conturile noi după
            pregătire nu sunt adăugate automat.
          </p>
          {selected.documentId && (
            <p className="notice-help">
              Versiunea anunțată rămâne blocată pentru editare. Activarea așteaptă finalizarea trimiterilor
              fără erori nerezolvate și cel puțin 15 zile de la ultima acceptare de către furnizor.
            </p>
          )}
          {selected.uncertain > 0 && (
            <p className="notice-warning" role="status">
              Unele trimiteri au rezultat incert. Verifică istoricul furnizorului înainte de orice
              retrimitere; acestea nu sunt reîncercate automat.
            </p>
          )}
          {selected.paused && (
            <p className="notice-warning" role="status">
              Trimiterea este pe pauză. Verifică erorile și data anunțată înainte să o reiei.
            </p>
          )}
          <p className="notice-help">
            La trimitere, salutul este personalizat cu prenumele fiecărui utilizator: „Bună, Ana!”.
            Previzualizarea arată salutul general.
          </p>
          <iframe
            title="Previzualizarea emailului"
            srcDoc={selected.html}
            sandbox=""
            referrerPolicy="no-referrer"
          />
          <div className="notice-actions">
            {(!selected.startedAt || selected.paused) && selected.pending > 0 && (
              <button
                type="button"
                className="admin-legal-primary"
                disabled={busy}
                onClick={() => setConfirmation('send')}
              >
                {selected.startedAt ? 'Reia trimiterea' : `Trimite către ${selected.recipientCount} conturi`}
              </button>
            )}
            {selected.startedAt && !selected.paused && selected.pending > 0 && (
              <button
                type="button"
                className="admin-legal-secondary"
                disabled={busy}
                onClick={pauseSelectedNotice}
              >
                Pune pe pauză
              </button>
            )}
            {selected.startedAt && selected.failed > 0 && (
              <button
                type="button"
                className="admin-legal-secondary"
                disabled={busy}
                onClick={() => setConfirmation('retry')}
              >
                Reîncearcă trimiterile respinse
              </button>
            )}
            {!selected.startedAt && (
              <button
                type="button"
                className="admin-legal-secondary"
                disabled={busy}
                onClick={() => setConfirmation('delete')}
              >
                Șterge ciorna
              </button>
            )}
          </div>
          <AdminServiceNoticeRecipients
            key={selected.id}
            notice={selected}
            busy={busy}
            onResolve={(recipientId, outcome) => setResolution({ recipientId, outcome })}
          />
        </div>
      )}
      <div className="notice-history">
        <h3>Istoricul informărilor</h3>
        {notices.length === 0 ? (
          <p>Nu ai pregătit încă nicio informare.</p>
        ) : (
          notices.map((n) => (
            <button
              key={n.id}
              type="button"
              disabled={busy}
              className={`notice-history-row${selectedId === n.id ? ' selected' : ''}`}
              onClick={() => setSelectedId(n.id)}
            >
              <span>
                <strong>{n.subject}</strong>
                <small>
                  {dateLabel(n.createdAt)} · {n.documentVersion ?? 'Închiderea platformei'}
                </small>
              </span>
              <span>
                {noticeStatusLabel(n)} · {n.accepted}/{n.recipientCount}
              </span>
            </button>
          ))
        )}
      </div>
      {confirmation && selected && (
        <ConfirmModal
          title={confirmation === 'delete' ? 'Ștergi ciorna?' : 'Confirmi trimiterea emailurilor?'}
          message={confirmationMessage(confirmation, selected)}
          confirmText={confirmation === 'delete' ? 'Șterge ciorna' : 'Confirmă trimiterea'}
          destructive
          isConfirming={busy}
          onCancel={() => setConfirmation(null)}
          onConfirm={confirmNoticeAction}
        />
      )}
      {resolution && selected && (
        <ConfirmModal
          title="Ai verificat istoricul furnizorului?"
          message={
            resolution.outcome === 'ACCEPTED'
              ? 'Confirmi că furnizorul a acceptat acest mesaj. Nu va fi retrimis. Preavizul este calculat conservator de la această confirmare.'
              : 'Confirmi că mesajul nu a fost acceptat de furnizor. Va putea fi retrimis prin opțiunea pentru trimiteri respinse.'
          }
          confirmText="Am verificat, confirm"
          isConfirming={busy}
          onCancel={() => setResolution(null)}
          onConfirm={confirmRecipientResolution}
        />
      )}
    </section>
  );
};

export default AdminServiceNotices;
